import React, { useState, useEffect } from 'react';
import styles from './AddPaymentModal.module.css';
import { db } from '../../../config/firebaseConfig';
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { FaMoney, FaPlus, FaTimes, FaMoneyBill, FaSave, FaTrash, FaInfoCircle } from 'react-icons/fa';
import { useTheme } from '../../../contexts/ThemeContext';

const AddPaymentModal = ({ onClose, onPaymentAdded, employeeId }) => {
    const { isDarkMode } = useTheme();
    const [paymentData, setPaymentData] = useState({
        paymentType: '',
        paymentDate: '',
        paidAmount: '',
        description: '',
        projectId: ''
    });
    const [projects, setProjects] = useState([]);

    const paymentTypes = [
        'Avans',
        'Prim',
        'Harcırah',
        'Yol-Yemek',
        'Diğer'
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setPaymentData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    useEffect(() => {
        const fetchEmployeeProjects = async () => {
            try {
                const projectsRef = collection(db, 'Projects');
                const querySnapshot = await getDocs(projectsRef);
                
                const projectsList = querySnapshot.docs
                    .map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }))
                    .filter(project => 
                        project.employees?.some(emp => emp.id === employeeId)
                    );
                
                setProjects(projectsList);
            } catch (error) {
                console.error('Error fetching projects:', error);
                toast.error('Projeler yüklenirken bir hata oluştu');
            }
        };

        if (employeeId) {
            fetchEmployeeProjects();
        }
    }, [employeeId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!paymentData.paymentType || !paymentData.paymentDate || 
            !paymentData.paidAmount || !paymentData.projectId) {
            toast.error('Lütfen zorunlu alanları doldurun');
            return;
        }

        try {
            const paymentRef = await addDoc(collection(db, 'Payments'), {
                employeeId,
                projectId: paymentData.projectId,
                paymentType: paymentData.paymentType,
                paymentDate: new Date(paymentData.paymentDate).toISOString(),
                paidAmount: Number(paymentData.paidAmount),
                description: paymentData.description,
                status: 'Ödendi',
                createdAt: new Date().toISOString()
            });

            toast.success('Ödeme başarıyla eklendi');
            onPaymentAdded();
            onClose();
        } catch (error) {
            console.error('Ödeme eklenirken hata:', error);
            toast.error('Ödeme eklenirken bir hata oluştu');
        }
    };

    const handleClearForm = () => {
        setPaymentData({
            paymentType: '',
            paymentDate: '',
            paidAmount: '',
            description: '',
            projectId: ''
        });
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return '0 ₺';
        return new Intl.NumberFormat('tr-TR', { 
            style: 'currency', 
            currency: 'TRY' 
        }).format(amount).replace('₺', '') + ' ₺';
    };

    return (
        <div className={styles.modalOverlay} onClick={handleOverlayClick}>
            <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>
                        <FaMoneyBill className={styles.modalIcon} />
                        Ödeme Ekle
                    </h2>
                    <button 
                        className={styles.closeButton} 
                        onClick={onClose}
                        aria-label="Kapat"
                    >
                        <FaTimes />
                    </button>
                </div>
                
                <div className={styles.modalBody}>
                    {projects.length === 0 && (
                        <div className={styles.infoMessage}>
                            <FaInfoCircle className={styles.infoIcon} />
                            <p>Bu çalışan henüz hiçbir projede yer almıyor. Ödeme eklemek için önce çalışanı bir projeye atamanız gerekir.</p>
                        </div>
                    )}
                
                    <form onSubmit={handleSubmit} className={styles.paymentForm} id="add-payment-form">
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Proje</label>
                            <select
                                name="projectId"
                                value={paymentData.projectId}
                                onChange={handleInputChange}
                                className={styles.formSelect}
                                required
                            >
                                <option value="">Lütfen Proje Seçiniz</option>
                                {projects.map(project => (
                                    <option key={project.id} value={project.id}>
                                        {project.titleShort}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Ödeme Türü</label>
                            <select
                                name="paymentType"
                                value={paymentData.paymentType}
                                onChange={handleInputChange}
                                className={styles.formSelect}
                                required
                            >
                                <option value="">Lütfen Seçiniz</option>
                                {paymentTypes.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Ödeme Tarihi</label>
                            <input
                                type="date"
                                name="paymentDate"
                                value={paymentData.paymentDate}
                                onChange={handleInputChange}
                                className={styles.formInput}
                                required
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Ödenen Miktar</label>
                            <input
                                type="number"
                                name="paidAmount"
                                value={paymentData.paidAmount}
                                onChange={handleInputChange}
                                className={`${styles.formInput} ${styles.inputNumber}`}
                                placeholder="0.00"
                                required
                                min="0"
                                step="0.01"
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Ödeme Gerekçesi</label>
                            <textarea
                                name="description"
                                value={paymentData.description}
                                onChange={handleInputChange}
                                className={styles.formTextarea}
                                placeholder="Ödeme açıklaması..."
                            />
                        </div>
                    </form>
                </div>
                
                <div className={styles.formActions}>
                    <button
                        type="button"
                        onClick={onClose}
                        className={styles.buttonCancel}
                    >
                        <FaTimes className={styles.buttonIcon} />
                        İptal
                    </button>
                    <button
                        type="button"
                        onClick={handleClearForm}
                        className={`${styles.buttonAction} ${styles.buttonWarning}`}
                    >
                        <FaTrash className={styles.buttonIcon} />
                        Temizle
                    </button>
                    <button
                        type="submit"
                        form="add-payment-form"
                        className={`${styles.buttonAction} ${styles.buttonSuccess}`}
                        disabled={projects.length === 0}
                    >
                        <FaSave className={styles.buttonIcon} />
                        Ödeme Ekle
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddPaymentModal;
